import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { markPageTourSeen } from './page-tours';
import type { TourHandle, TourNavigation } from './tour-engine';
import {
    WELCOME_TOUR_HOME_PATH,
    WELCOME_TOUR_START_EVENT,
    markTourSeen,
    requestWelcomeTour,
    resolveTourSeen,
    startWelcomeTour,
} from './welcome-tour';

/** Sayfa yerlesimi (kartlar, gorseller) otursun diye tur kisa bir gecikmeyle acilir. */
export const WELCOME_TOUR_START_DELAY_MS = 600;

type TourLocationState = { startTour?: boolean } | null;

/**
 * Herhangi bir yerden turu yeniden baslatir: anasayfadaysa olay yayinlar,
 * degilse anasayfaya `startTour` durumuyla gider (tur yoneticisinin olmadigi
 * sayfalardan da calissin diye). Tur goruldu bilgisinden bagimsizdir.
 */
export const useStartWelcomeTour = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    return useCallback(() => {
        if (pathname === WELCOME_TOUR_HOME_PATH) {
            requestWelcomeTour();
            return;
        }
        navigate(WELCOME_TOUR_HOME_PATH, { state: { startTour: true } });
    }, [navigate, pathname]);
};

type UseWelcomeTourAutoStartOptions = {
    userId: string;
    /** Yeni kullaniciya bir kez otomatik gosterilsin mi? (orn. yonetici icin `false`) */
    autoStart: boolean;
    /** Anasayfa cizildi mi (ve ust uste binecek pencere yok mu)? */
    ready: boolean;
};

/** Anasayfada: tur daha once gorulmediyse bir kez baslatma istegi gonderir. */
export const useWelcomeTourAutoStart = ({ userId, autoStart, ready }: UseWelcomeTourAutoStartOptions) => {
    const checkedRef = useRef(false);

    useEffect(() => {
        if (!autoStart || !ready || !userId || checkedRef.current) return undefined;
        checkedRef.current = true;
        let active = true;
        resolveTourSeen(userId).then((seen) => {
            if (active && !seen) requestWelcomeTour();
        });
        return () => {
            active = false;
        };
    }, [autoStart, ready, userId]);
};

/**
 * Karsilama turunu yonetir (ana yerlesimde bir kez kullanilir, sayfa
 * degisince kaybolmaz). Baslatma istegini (olay ya da gezinme durumu)
 * karsilar ve tura sayfalar arasi gezinmeyi saglar.
 *
 * Tur bitince ya da atlaninca "goruldu" isaretlenir ve bastan sona gezilen
 * sayfalarin sayfa turlari da gorulmus sayilir. Kullanici tur surerken kendisi
 * baska sayfaya giderse (tarayici geri vb.) tur kapanir ama gorulmus sayilmaz.
 */
export const useWelcomeTour = ({ userId }: { userId: string }) => {
    const { t } = useI18n();
    const location = useLocation();
    const navigate = useNavigate();
    const [requested, setRequested] = useState(false);
    const tourRef = useRef<TourHandle | null>(null);
    const abandonedRef = useRef(false);
    const pathnameRef = useRef(location.pathname);
    pathnameRef.current = location.pathname;
    const pendingNavigationRef = useRef<(() => void) | null>(null);

    const navigation = useMemo<TourNavigation>(
        () => ({
            currentPath: () => pathnameRef.current,
            navigate: (path) =>
                new Promise<void>((resolve) => {
                    pendingNavigationRef.current = resolve;
                    navigate(path);
                }),
        }),
        [navigate],
    );

    useEffect(() => {
        if (!(location.state as TourLocationState)?.startTour) return;
        setRequested(true);
        // Durum temizlenir; sayfa yenilenince tur tekrar acilmaz.
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }, [location.state, location.pathname, location.search, navigate]);

    useEffect(() => {
        const handleRequest = () => setRequested(true);
        window.addEventListener(WELCOME_TOUR_START_EVENT, handleRequest);
        return () => window.removeEventListener(WELCOME_TOUR_START_EVENT, handleRequest);
    }, []);

    useEffect(() => {
        if (!requested) return undefined;
        const timer = window.setTimeout(() => {
            setRequested(false);
            tourRef.current?.destroy();
            abandonedRef.current = false;
            tourRef.current = startWelcomeTour({
                t,
                navigation,
                onClose: ({ coveredPageIds }) => {
                    tourRef.current = null;
                    pendingNavigationRef.current = null;
                    if (abandonedRef.current) return;
                    markTourSeen(userId);
                    coveredPageIds.forEach((pageId) => markPageTourSeen(pageId, userId));
                },
            });
        }, WELCOME_TOUR_START_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [requested, t, navigation, userId]);

    useEffect(() => {
        // Yeni sayfa cizildi: turun kendi gezinmesiyse tur devam eder.
        const resolveNavigation = pendingNavigationRef.current;
        if (resolveNavigation) {
            pendingNavigationRef.current = null;
            resolveNavigation();
            return;
        }
        // Tur adim hazirlarken olan degisiklik (orn. sayfanin yonlendirmesi) turundur;
        // adim gosterilirken olan degisiklik kullanicinindir.
        const tour = tourRef.current;
        if (tour && !tour.isBusy()) {
            abandonedRef.current = true;
            tour.destroy();
        }
    }, [location.pathname]);

    useEffect(
        () => () => {
            abandonedRef.current = true;
            tourRef.current?.destroy();
        },
        [],
    );
};
